import { format } from "date-fns";
import type { TFunction } from "i18next";
import { z } from "zod";

export const MAX_TEXT_LENGTH = 1000;
export const COMPANY_ID_LENGTH = 5;

type ApproveLicenseSchemaOptions = {
  requireCompanyId?: boolean;
};

const parseLocalDate = (value: string) => new Date(`${value}T00:00:00`);

const dateValue = z
  .union([z.string(), z.date(), z.null()])
  .transform((value) => (value instanceof Date ? format(value, "yyyy-MM-dd") : (value ?? "")));

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const approveLicenseSchema = (
  t: TFunction,
  { requireCompanyId = true }: ApproveLicenseSchemaOptions = {},
) =>
  z
    .object({
      startDate: dateValue.pipe(
        z
          .string()
          .min(
            1,
            t("licensing.form-validation.mandatory", {
              label: t("licensing.access-enabled-from"),
            }),
          )
          .refine((v) => parseLocalDate(v) >= startOfToday(), {
            message: t("licensing.form-validation.date-not-in-past", {
              label: t("licensing.access-enabled-from"),
            }),
          }),
      ),

      expiryDate: dateValue.pipe(
        z
          .string()
          .min(
            1,
            t("licensing.form-validation.mandatory", {
              label: t("licensing.access-enabled-until"),
            }),
          )
          .refine((v) => parseLocalDate(v) >= startOfToday(), {
            message: t("licensing.form-validation.date-not-in-past", {
              label: t("licensing.access-enabled-until"),
            }),
          }),
      ),

      numberOfAccountsAllowed: z
        .number()
        .int()
        .min(
          1,
          t("licensing.form-validation.mandatory", {
            label: t("licensing.no-of-accounts-allowed"),
          }),
        ),

      companyId: requireCompanyId
        ? z
            .string()
            .min(
              1,
              t("licensing.form-validation.company-id-mandatory", {
                label: t("licensing.company-id"),
              }),
            )
            .refine((v) => v.length === COMPANY_ID_LENGTH, {
              message: t("licensing.form-validation.exact-length", {
                label: t("licensing.company-id"),
                length: COMPANY_ID_LENGTH,
              }),
            })
            .refine((v) => /^[A-Z]{5}$/.test(v), {
              message: t("licensing.form-validation.invalid-company-id", {
                label: t("licensing.company-id"),
              }),
            })
        : z.string(),

      approvalNotes: z
        .string()
        .max(
          MAX_TEXT_LENGTH,
          t("licensing.form-validation.max-length", {
            label: t("licensing.notes"),
            max: MAX_TEXT_LENGTH,
          }),
        )
        .optional()
        .or(z.literal("")),
    })
    .refine(
      (data) => {
        if (!data.startDate || !data.expiryDate) return true;
        const from = parseLocalDate(data.startDate);
        const to = parseLocalDate(data.expiryDate);
        return to >= from;
      },
      {
        path: ["expiryDate"],
        message: t("licensing.form-validation.date-to-not-before-from", {
          fromLabel: t("licensing.access-enabled-from"),
          toLabel: t("licensing.access-enabled-until"),
        }),
      },
    );

export type ApproveLicenseSchema = z.infer<ReturnType<typeof approveLicenseSchema>>;
export type ApproveLicenseFormData = z.input<ReturnType<typeof approveLicenseSchema>>;
